import { gsap } from 'gsap';
import { BaseEffectModule } from '../BaseEffectModule.js';

/**
 * Parallax Module - VIEWPORT OPTIMIZED (1:1 copy of MouseDistanceModule system)
 * ✅ Exact same viewport optimization as MouseDistanceModule
 * ✅ Simple boolean isInAnimationZone (not complex enum zones)
 * ✅ Same buffer calculations and early exit logic
 * ✅ Proven performance system that actually works
 */
export class ParallaxModule extends BaseEffectModule {
    constructor() {
        super('parallax', {
            version: '4.0.0',
            description: 'Parallax effects - VIEWPORT OPTIMIZED (MouseDistanceModule system)'
        });
        
        // ✅ EXACT COPY: Same viewport optimization as MouseDistanceModule
        this.VIEWPORT_OPTIMIZATION = {
            enabled: true,
            buffer: 100,
            includeElementSize: true,
            checkInterval: 100,
            debugMode: false
        };
        
        this.DEBUG_MODE = false;
    }
    
    createState(element, config) {
        const parallaxConfig = {
            scrollParallax: {
                enabled: config.useParallax || false,
                speed: config.parallaxSpeed || 0.5,
                direction: config.parallaxDirection || 'vertical',
                invertDirection: config.invertParallaxDirection || false
            },
            mouseParallax: {
                enabled: config.useMouseParallax || false,
                speed: config.mouseParallaxSpeed || 0.1,
                direction: config.mouseParallaxDirection || 'both',
                invertDirection: config.invertMouseParallaxDirection || false,
                smoothing: config.mouseParallaxSmoothness || 0.15
            }
        };
        
        if (!parallaxConfig.scrollParallax.enabled && !parallaxConfig.mouseParallax.enabled) {
            return {
                enabled: false,
                reason: 'No parallax effects enabled'
            };
        }
        
        // ✅ Initialize GSAP for the element
        gsap.set(element, { 
            opacity: 1,
            x: 0,
            y: 0,
            force3D: true
        });
        
        const state = {
            enabled: true,
            config: parallaxConfig,
            
            // ✅ Position values
            currentX: 0,
            currentY: 0,
            targetX: 0,
            targetY: 0,
            
            // ✅ EXACT COPY: Same viewport state as MouseDistanceModule
            viewportState: {
                isInAnimationZone: true,        // Initially assume visible
                lastViewportCheck: 0,           // Last viewport check timestamp
                animationZoneRect: null,        // Cached animation zone bounds
                wasOutOfZone: false,            // Track state changes
                checksDue: 0                    // Performance: skip checks counter
            },
            
            // ✅ Cached data
            elementCenter: { x: 0, y: 0 },
            elementCenterLastUpdate: 0,
            
            // ✅ Performance optimization
            lastUpdate: 0,
            updateThreshold: 16, // ~60fps
            
            // Activity tracking
            isVisible: true,
            isActive: true,
            needsUpdate: false
        };
        
        // ✅ EXACT COPY: Initialize viewport optimization like MouseDistanceModule
        this.initializeViewportOptimization(element, state);
        
        // Add CSS classes for debugging
        if (parallaxConfig.scrollParallax.enabled) {
            element.classList.add('unified-scroll-parallax');
        }
        if (parallaxConfig.mouseParallax.enabled) {
            element.classList.add('unified-mouse-parallax');
        }
        
        if (this.DEBUG_MODE) {
            console.log('🌊 ParallaxModule VIEWPORT OPTIMIZED initialized:', element.id);
        }
        
        return state;
    }
    
    /**
     * ✅ EXACT COPY: Initialize viewport optimization (from MouseDistanceModule)
     */
    initializeViewportOptimization(element, state) {
        // Calculate initial animation zone
        this.updateAnimationZone(element, state);
        
        // Mark element for viewport tracking
        element.classList.add('viewport-optimized-parallax');
    }
    
    /**
     * ✅ EXACT COPY: Update animation zone calculation (from MouseDistanceModule)
     */
    updateAnimationZone(element, state) {
        const viewportState = state.viewportState;
        const vpOpt = this.VIEWPORT_OPTIMIZATION;
        
        const rect = element.getBoundingClientRect();
        const viewport = {
            width: window.innerWidth,
            height: window.innerHeight
        };
        
        // Calculate effective buffer including element size
        let effectiveBuffer = vpOpt.buffer;
        if (vpOpt.includeElementSize) {
            effectiveBuffer += Math.max(rect.width, rect.height);
        }
        
        // Calculate animation zone bounds
        const animationZone = {
            left: -effectiveBuffer,
            top: -effectiveBuffer,
            right: viewport.width + effectiveBuffer,
            bottom: viewport.height + effectiveBuffer
        };
        
        // Check if element intersects with animation zone
        const isInZone = !(
            rect.right < animationZone.left ||
            rect.left > animationZone.right ||
            rect.bottom < animationZone.top ||
            rect.top > animationZone.bottom
        );
        
        // Update state
        const wasInZone = viewportState.isInAnimationZone;
        viewportState.isInAnimationZone = isInZone;
        viewportState.animationZoneRect = animationZone;
        
        // Track major state changes for optimization
        if (wasInZone && !isInZone) {
            viewportState.wasOutOfZone = true;
        } else if (!wasInZone && isInZone) {
            viewportState.wasOutOfZone = false;
        }
    }
    
    /**
     * ✅ EXACT COPY: Check if element should be animated (from MouseDistanceModule)
     */
    shouldAnimateElement(element, state, currentTime) {
        const viewportState = state.viewportState;
        const vpOpt = this.VIEWPORT_OPTIMIZATION;
        
        // ✅ PERFORMANCE: Skip viewport checks when element is stable out of zone
        if (viewportState.wasOutOfZone && viewportState.checksDue > 0) {
            viewportState.checksDue--;
            return false; // Still out of zone, skip expensive checks
        }
        
        // ✅ PERFORMANCE: Update viewport check periodically
        if (currentTime - viewportState.lastViewportCheck > vpOpt.checkInterval) {
            this.updateAnimationZone(element, state);
            viewportState.lastViewportCheck = currentTime;
            
            // ✅ OPTIMIZATION: If element went out of zone, delay future checks
            if (!viewportState.isInAnimationZone) {
                viewportState.checksDue = 5; // Skip next 5 checks (~500ms)
            }
        }
        
        return viewportState.isInAnimationZone;
    }
    
    /**
     * ✅ EXACT COPY: Main update with viewport optimization (from MouseDistanceModule logic)
     */
    update(element, state, sharedState, deltaTime, cache) {
        if (!this.isEnabled || !state.enabled || !state.isActive) return;
        
        // ✅ VIEWPORT OPTIMIZATION: Early exit if out of animation zone
        if (!this.shouldAnimateElement(element, state, sharedState.timestamp)) {
            // Element is outside animation zone - skip expensive calculations
            // Optionally reset to initial state when leaving zone
            if (state.currentX !== 0 || state.currentY !== 0) {
                state.targetX = 0;
                state.targetY = 0;
                state.currentX = 0;
                state.currentY = 0;
                gsap.set(element, { x: 0, y: 0, force3D: true });
            }
            return;
        }
        
        // Performance throttling
        if (sharedState.timestamp - state.lastUpdate < state.updateThreshold) {
            return;
        }
        
        let hasChanges = false;
        
        // ✅ OPTIMIZED: Update element center less frequently
        this.updateElementCenter(state, cache, sharedState.timestamp);
        
        // ✅ SCROLL PARALLAX: Use shared scroll state
        if (state.config.scrollParallax.enabled) {
            hasChanges = this.updateScrollParallax(state, sharedState) || hasChanges;
        }
        
        // ✅ MOUSE PARALLAX: Use shared mouse position (with smooth startup!)
        if (state.config.mouseParallax.enabled) {
            hasChanges = this.updateMouseParallax(state, sharedState) || hasChanges;
        }
        
        // ✅ APPLY: Transform only when needed
        if (hasChanges || state.needsUpdate) {
            this.applyTransform(element, state);
            state.needsUpdate = false;
        }
        
        state.lastUpdate = sharedState.timestamp;
    }
    
    /**
     * ✅ OPTIMIZED: Update element center less frequently
     */
    updateElementCenter(state, cache, timestamp) {
        // Update element center only every 100ms or when cache changes
        if (cache.rect && (timestamp - state.elementCenterLastUpdate > 100)) {
            state.elementCenter.x = cache.rect.left + cache.rect.width / 2;
            state.elementCenter.y = cache.rect.top + cache.rect.height / 2;
            state.elementCenterLastUpdate = timestamp;
        }
    }
    
    /**
     * ✅ SCROLL PARALLAX: Clean and efficient
     */
    updateScrollParallax(state, sharedState) {
        const config = state.config.scrollParallax;
        const windowHeight = sharedState.viewport.height;
        const windowWidth = sharedState.viewport.width;
        
        let changed = false;
        
        // Vertical parallax
        if (config.direction === 'vertical' || config.direction === 'both') {
            const windowMiddleY = windowHeight / 2;
            const percentY = (state.elementCenter.y - windowMiddleY) / windowHeight;
            
            const directionMultiplier = config.invertDirection ? -1 : 1;
            const newTargetY = percentY * config.speed * 100 * directionMultiplier;
            
            if (Math.abs(state.targetY - newTargetY) > 0.1) {
                state.targetY = newTargetY;
                changed = true;
            }
        }
        
        // Horizontal parallax
        if (config.direction === 'horizontal' || config.direction === 'both') {
            const windowMiddleX = windowWidth / 2;
            const percentX = (state.elementCenter.x - windowMiddleX) / windowWidth;
            
            const directionMultiplier = config.invertDirection ? -1 : 1;
            const newTargetX = percentX * config.speed * 100 * directionMultiplier;
            
            if (Math.abs(state.targetX - newTargetX) > 0.1) {
                state.targetX = newTargetX;
                changed = true;
            }
        }
        
        return changed;
    }
    
    /**
     * ✅ MOUSE PARALLAX: Uses global smooth mouse position!
     */
    updateMouseParallax(state, sharedState) {
        const config = state.config.mouseParallax;
        
        // ✅ CRITICAL: Use sharedState.mousePosition (has smooth startup!)
        const mousePos = sharedState.mousePosition;
        
        // Calculate delta from mouse to element center
        const deltaX = mousePos.x - state.elementCenter.x;
        const deltaY = mousePos.y - state.elementCenter.y;
        
        // Apply speed multiplier
        let newTargetX = state.targetX; // Keep current if not changing
        let newTargetY = state.targetY;
        
        // Apply direction settings
        if (config.direction === 'horizontal' || config.direction === 'both') {
            let calculatedX = deltaX * config.speed;
            if (config.invertDirection) calculatedX = -calculatedX;
            newTargetX = calculatedX;
        }
        
        if (config.direction === 'vertical' || config.direction === 'both') {
            let calculatedY = deltaY * config.speed;
            if (config.invertDirection) calculatedY = -calculatedY;
            newTargetY = calculatedY;
        }
        
        // Check if targets changed significantly
        const changedX = Math.abs(state.targetX - newTargetX) > 0.1;
        const changedY = Math.abs(state.targetY - newTargetY) > 0.1;
        
        if (changedX) state.targetX = newTargetX;
        if (changedY) state.targetY = newTargetY;
        
        return changedX || changedY;
    }
    
    /**
     * ✅ CRITICAL: Apply transform with smooth interpolation
     */
    applyTransform(element, state) {
        const config = state.config.mouseParallax;
        const smoothing = config.smoothing || 0.15;
        
        // ✅ SMOOTH INTERPOLATION: Like old working code
        state.currentX += (state.targetX - state.currentX) * smoothing;
        state.currentY += (state.targetY - state.currentY) * smoothing;
        
        // ✅ SINGLE GSAP.SET: Optimized
        gsap.set(element, {
            x: state.currentX,
            y: state.currentY,
            force3D: true
        });
        
        if (this.DEBUG_MODE) {
            console.log('🌊 Applied parallax transform:', element.id, {
                currentX: state.currentX.toFixed(2),
                currentY: state.currentY.toFixed(2),
                targetX: state.targetX.toFixed(2),
                targetY: state.targetY.toFixed(2),
                inZone: state.viewportState.isInAnimationZone
            });
        }
    }
    
    /**
     * ✅ PERFORMANCE: Enable/disable based on visibility
     */
    setActive(state, active) {
        state.isActive = active;
        if (this.DEBUG_MODE) {
            console.log('🌊 Parallax active state:', active);
        }
    }
    
    /**
     * ✅ SMOOTH RESET: Clean reset
     */
    reset(element, state) {
        state.currentX = 0;
        state.currentY = 0;
        state.targetX = 0;
        state.targetY = 0;
        state.needsUpdate = false;
        
        gsap.set(element, { x: 0, y: 0 });
        
        if (this.DEBUG_MODE) {
            console.log('🔄 Parallax reset:', element.id);
        }
    }
    
    /**
     * ✅ Get viewport optimization stats
     */
    getViewportStats(state) {
        return {
            isInAnimationZone: state.viewportState?.isInAnimationZone || true,
            wasOutOfZone: state.viewportState?.wasOutOfZone || false,
            checksDue: state.viewportState?.checksDue || 0,
            lastCheck: state.viewportState?.lastViewportCheck || 0,
            animationZone: state.viewportState?.animationZoneRect || null,
            buffer: this.VIEWPORT_OPTIMIZATION.buffer,
            includeElementSize: this.VIEWPORT_OPTIMIZATION.includeElementSize
        };
    }
    
    /**
     * ✅ CLEANUP: Proper cleanup
     */
    cleanup(state) {
        // ✅ Cleanup viewport optimization
        if (state.element) {
            state.element.classList.remove('viewport-optimized-parallax');
        }
        
        // Standard cleanup
        state.config = null;
        state.elementCenter = null;
        state.viewportState = null;
        
        if (this.DEBUG_MODE) {
            console.log('🗑️ Viewport-optimized parallax cleaned up');
        }
    }
    
    /**
     * ✅ DEBUG: Enable/disable debug logging
     */
    setDebugMode(enabled) {
        this.DEBUG_MODE = enabled;
        console.log(`🐛 Parallax debug mode: ${enabled ? 'ON' : 'OFF'}`);
    }
    
    /**
     * ✅ NEW: Force update (useful for testing)
     */
    forceUpdate(element, state) {
        state.needsUpdate = true;
    }
    
    /**
     * ✅ NEW: Get current parallax values
     */
    getCurrentValues(state) {
        return {
            current: { x: state.currentX, y: state.currentY },
            target: { x: state.targetX, y: state.targetY },
            elementCenter: { ...state.elementCenter }
        };
    }
    
    /**
     * ✅ NEW: Set custom parallax values (for advanced usage)
     */
    setCustomValues(state, x, y) {
        state.targetX = x;
        state.targetY = y;
        state.needsUpdate = true;
    }
}